// ─────────────────────────────────────────────────────────────────────────────
// DemoEditorDrawer.jsx — your control panel for the demo dataset.
//
// This is the piece that exists because photos and timestamps cannot be
// authored ahead of time. Everything it writes goes to demoStore (localStorage
// for fields, IndexedDB for image blobs) and is layered over the seed, so an
// edit survives reload and "Reset" always gets you back to a clean demo.
//
// It is NOT part of the demo surface. It only mounts when the edit toggle is on
// (Ctrl+Shift+E), which is off by default so it cannot appear mid-presentation.
//
// PHOTOS — three sources, because none alone is sufficient:
//   Server gallery  ~360 real filenames baked into demoPhotoManifest, browsable
//                   by folder. No directory-listing endpoint exists and we are
//                   not adding one, so the list is harvested at build time.
//   Local folder    <input webkitdirectory> over a folder synced off the VPS.
//                   Works with no internet, which matters on a demo laptop.
//   Single upload   drag or pick one file. Downscaled to 1200px before storage.
//
// TIME — every instant field is edited in Asia/Karachi wall time and stored as
// UTC, matching the workspace convention. Out-of-order chains are flagged
// inline rather than blocked: sometimes you genuinely want an odd sequence to
// demonstrate what the page does with it.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  X, Image as ImageIcon, Clock, Users, Database, Upload, FolderOpen,
  Trash2, RotateCcw, Download, Check, AlertTriangle, Search, Zap,
} from 'lucide-react';
import {
  getData, getSeedData, setOverride, clearOverrideField, setPhotoSlot,
  applyPhotoToClient, setPhotoMode, getPhotoMode, clearPhotoOverrides,
  putPhoto, resolvePhoto, subscribe, exportJSON, importJSON, resetAll,
  photoStorageBytes, photoCount, cacheAllPhotos, cacheServerPhoto,
  clearServerCache, referencedServerPhotos, isCached,
} from './demoStore';
import {
  PHOTO_FOLDERS, PHOTO_LIBRARY, BAKED_LIBRARY, BAKED_COUNT, folderForSlot,
} from './demoPhotoManifest';
import { demoToast } from './installDemoAdapter';

const PKT_MS = 5 * 3600 * 1000;

/** ISO UTC -> 'YYYY-MM-DDTHH:mm' in Asia/Karachi, for <input type="datetime-local">. */
const toPktInput = (iso) => {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  return new Date(t + PKT_MS).toISOString().slice(0, 16);
};

/** 'YYYY-MM-DDTHH:mm' in Asia/Karachi -> ISO UTC. */
const fromPktInput = (val) => {
  if (!val) return null;
  const t = Date.parse(`${val}:00Z`);
  if (Number.isNaN(t)) return null;
  return new Date(t - PKT_MS).toISOString();
};

const fmtBytes = (n) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

/** The instant fields on a job, in the order they must occur. */
const JOB_TIME_FIELDS = [
  ['reached_location_at', 'Reached site'],
  ['started_at', 'Work started'],
  ['before_photos_at', 'Before photos'],
  ['after_photos_at', 'After photos'],
  ['completed_at', 'Completed'],
];

const JOB_PHOTO_FIELDS = [
  ['before_photos', 'Before photos', true],
  ['after_photos', 'After photos', true],
  ['temp_voltage_photos', 'Temp / voltage', true],
  ['fsr_photo', 'FSR photo', false],
  ['customer_signature', 'Signature', false],
];

const JOB_TEXT_FIELDS = [
  ['site_name', 'Site name'],
  ['notes', 'Notes'],
  ['panel_brand', 'Panel brand'],
  ['panel_damage', 'Panel damage'],
  ['mos_and_debris', 'Soiling'],
  ['bird_dropping', 'Bird droppings'],
  ['rusting', 'Rusting'],
  ['earthing', 'Earthing'],
  ['additional_notes', 'Additional notes'],
];

// ─── Shared styling ──────────────────────────────────────────────────────────

const S = {
  panel: {
    position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(560px, 100vw)',
    background: 'var(--nd-bg, #0B0D11)', borderLeft: '1px solid var(--nd-w12, rgba(255,255,255,0.12))',
    zIndex: 5000, display: 'flex', flexDirection: 'column',
    fontFamily: "'DM Sans', system-ui, sans-serif", color: 'var(--nd-text, #E6E8EC)',
    boxShadow: '-18px 0 48px rgba(0,0,0,0.45)',
  },
  scrim: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 4990 },
  head: {
    padding: '16px 18px', borderBottom: '1px solid var(--nd-w08, rgba(255,255,255,0.08))',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
  },
  tabs: {
    display: 'flex', gap: 4, padding: '10px 12px',
    borderBottom: '1px solid var(--nd-w06, rgba(255,255,255,0.06))', flexShrink: 0,
  },
  body: { flex: 1, overflowY: 'auto', padding: '14px 18px 40px' },
  label: {
    fontSize: 9, fontWeight: 700, letterSpacing: '0.13em', textTransform: 'uppercase',
    color: 'var(--nd-tx35, rgba(230,232,236,0.35))', marginBottom: 6, display: 'block',
  },
  input: {
    width: '100%', background: 'var(--nd-w05, rgba(255,255,255,0.05))',
    border: '1px solid var(--nd-w12, rgba(255,255,255,0.12))', borderRadius: 8,
    color: 'var(--nd-text, #E6E8EC)', fontSize: 13, padding: '7px 9px', outline: 'none',
    fontFamily: 'inherit',
  },
  card: {
    background: 'var(--nd-surface, #14171C)', border: '1px solid var(--nd-w06, rgba(255,255,255,0.06))',
    borderRadius: 12, padding: 14, marginBottom: 12,
  },
  section: { fontSize: 12, fontWeight: 800, marginBottom: 10, letterSpacing: '-0.01em' },
  hint: { fontSize: 11, color: 'var(--nd-tx40, rgba(230,232,236,0.4))', lineHeight: 1.5 },
};

const tabBtn = (on) => ({
  flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  background: on ? 'rgba(96,165,250,0.15)' : 'transparent',
  border: `1px solid ${on ? 'rgba(96,165,250,0.35)' : 'transparent'}`,
  color: on ? '#60A5FA' : 'var(--nd-tx40, rgba(230,232,236,0.4))',
  borderRadius: 8, padding: '7px 6px', fontSize: 11, fontWeight: 700, cursor: 'pointer',
  fontFamily: 'inherit',
});

const btn = (tone = 'plain') => {
  const map = {
    plain: ['rgba(255,255,255,0.06)', 'rgba(255,255,255,0.14)', 'var(--nd-text, #E6E8EC)'],
    blue: ['rgba(96,165,250,0.15)', 'rgba(96,165,250,0.38)', '#60A5FA'],
    red: ['rgba(248,113,113,0.14)', 'rgba(248,113,113,0.38)', '#F87171'],
    green: ['rgba(34,211,165,0.14)', 'rgba(34,211,165,0.38)', '#22D3A5'],
  };
  const [bg, border, fg] = map[tone] || map.plain;
  return {
    display: 'inline-flex', alignItems: 'center', gap: 6, background: bg,
    border: `1px solid ${border}`, color: fg, borderRadius: 8, padding: '6px 11px',
    fontSize: 11.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
  };
};

// ─── Photo picker ────────────────────────────────────────────────────────────

/**
 * Modal for choosing one image. Opens on the folder that matches the slot being
 * filled, so you are not hunting for the right tab every time.
 */
const PAGE_SIZE = 12;

const PhotoPicker = ({ slot, onPick, onClose }) => {
  // Opens on the baked set: it is instant, and for most edits it is all you need.
  // The server-original folders are there for when you want a specific photo.
  const [folder, setFolder] = useState('__baked');
  const [local, setLocal] = useState([]); // {name, url, file}
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(0);
  const localRef = useRef(null);
  const fileRef = useRef(null);

  // Object URLs for the local-folder preview are ours to clean up.
  useEffect(() => () => local.forEach((f) => URL.revokeObjectURL(f.url)), [local]);

  const onLocalFolder = (e) => {
    const files = [...(e.target.files || [])]
      .filter((f) => /^image\//.test(f.type))
      .slice(0, 300); // a big uploads folder would otherwise lock the tab up
    setLocal(files.map((f) => ({
      name: f.webkitRelativePath || f.name,
      url: URL.createObjectURL(f),
      file: f,
    })));
  };

  const takeFile = async (file) => {
    setBusy(true);
    try {
      const key = await putPhoto(file);
      onPick(key);
      onClose();
    } catch (err) {
      demoToast(`Could not store that image: ${err.message}`, 'warn');
    } finally {
      setBusy(false);
    }
  };

  // Picking a server photo caches a downscaled copy, so the slot renders from a
  // small local blob rather than re-fetching a multi-megabyte original.
  const takeServer = async (path) => {
    setBusy(true);
    try {
      await cacheServerPhoto(path);
    } catch (_) {
      // Caching is an optimisation; the raw path still renders, just slowly.
    } finally {
      setBusy(false);
      onPick(path);
      onClose();
    }
  };

  const isBaked = folder === '__baked';

  // The baked set is the folder-for-this-slot's local copies, plus everything
  // else after it so you are never blocked from using another folder's photo.
  const bakedList = (() => {
    const primary = folderForSlot(slot);
    const rest = Object.keys(BAKED_LIBRARY).filter((k) => k !== primary);
    return [primary, ...rest].reduce((acc, k) => acc.concat(BAKED_LIBRARY[k] || []), []);
  })();

  const rawList = isBaked ? bakedList : (PHOTO_LIBRARY[folder] || []);
  const serverList = rawList.filter(
    (p) => !filter || p.toLowerCase().includes(filter.toLowerCase()),
  );

  // Server originals are paginated on purpose: 0.8–4.7 MB each with no thumbnail
  // variant, so rendering all 120 queues ~320 MB behind the browser's
  // six-connections-per-host limit and nothing ever appears. The baked set is
  // ~80 KB apiece, so it can all be shown at once.
  const pageSize = isBaked ? bakedList.length : PAGE_SIZE;
  const pageCount = Math.max(1, Math.ceil(serverList.length / pageSize));
  const safePage = Math.min(page, pageCount - 1);
  const pageItems = serverList.slice(safePage * pageSize, safePage * pageSize + pageSize);

  return (
    <>
      <div style={{ ...S.scrim, zIndex: 5100 }} onClick={onClose} />
      <div
        style={{
          position: 'fixed', top: '6vh', left: '50%', transform: 'translateX(-50%)',
          width: 'min(760px, 94vw)', maxHeight: '88vh', background: 'var(--nd-bg, #0B0D11)',
          border: '1px solid var(--nd-w12, rgba(255,255,255,0.12))', borderRadius: 14,
          zIndex: 5110, display: 'flex', flexDirection: 'column', overflow: 'hidden',
          fontFamily: "'DM Sans', system-ui, sans-serif", color: 'var(--nd-text, #E6E8EC)',
        }}
      >
        <div style={S.head}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800 }}>Choose a photo</div>
            <div style={S.hint}>Filling: {slot.replace(/_/g, ' ')}</div>
          </div>
          <button type="button" onClick={onClose} style={btn()}><X size={13} /> Close</button>
        </div>

        <div style={{ ...S.tabs, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => { setFolder('__baked'); setPage(0); }}
            style={{ ...tabBtn(folder === '__baked'), flex: '1 1 150px' }}
            title="Pre-downscaled photos bundled with the app. Instant, no download."
          >
            <Zap size={12} /> Demo set
          </button>
          {PHOTO_FOLDERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => { setFolder(f.key); setPage(0); }}
              style={{ ...tabBtn(folder === f.key), flex: '1 1 150px' }}
              title={f.hint}
            >
              {f.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setFolder('__local')}
            style={{ ...tabBtn(folder === '__local'), flex: '1 1 150px' }}
            title="Browse a folder on this machine"
          >
            <FolderOpen size={12} /> My folder
          </button>
        </div>

        <div style={{ padding: '10px 16px', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {folder === '__local' ? (
            <>
              <button type="button" style={btn('blue')} onClick={() => localRef.current && localRef.current.click()}>
                <FolderOpen size={13} /> Pick a folder
              </button>
              <span style={S.hint}>
                {local.length ? `${local.length} image(s) — click one to use it` : 'Point at an uploads folder synced from the VPS. Nothing leaves this machine.'}
              </span>
              <input
                ref={localRef}
                type="file"
                webkitdirectory=""
                directory=""
                multiple
                accept="image/*"
                onChange={onLocalFolder}
                style={{ display: 'none' }}
              />
            </>
          ) : (
            <>
              <div style={{ position: 'relative', flex: '1 1 200px' }}>
                <Search size={13} style={{ position: 'absolute', left: 9, top: 9, opacity: 0.4 }} />
                <input
                  style={{ ...S.input, paddingLeft: 28 }}
                  placeholder="Filter by filename…"
                  value={filter}
                  onChange={(e) => { setFilter(e.target.value); setPage(0); }}
                />
              </div>
              <button type="button" style={btn()} onClick={() => fileRef.current && fileRef.current.click()}>
                <Upload size={13} /> Upload one
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                onChange={(e) => e.target.files && e.target.files[0] && takeFile(e.target.files[0])}
                style={{ display: 'none' }}
              />
            </>
          )}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '4px 16px 18px' }}>
          {busy && <div style={S.hint}>Storing image…</div>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(104px, 1fr))', gap: 8 }}>
            {folder === '__local'
              ? local.map((f) => (
                <button
                  key={f.name}
                  type="button"
                  onClick={() => takeFile(f.file)}
                  title={f.name}
                  style={{ padding: 0, border: '1px solid var(--nd-w12, rgba(255,255,255,0.12))', borderRadius: 8, overflow: 'hidden', cursor: 'pointer', background: 'none' }}
                >
                  <img src={f.url} alt={f.name} loading="lazy" style={{ width: '100%', height: 84, objectFit: 'cover', display: 'block' }} />
                </button>
              ))
              : pageItems.map((p) => (
                <button
                  key={p}
                  type="button"
                  // Baked photos are already local — no caching round-trip needed.
                  onClick={() => (isBaked ? (onPick(p), onClose()) : takeServer(p))}
                  title={p}
                  style={{ position: 'relative', padding: 0, border: '1px solid var(--nd-w12, rgba(255,255,255,0.12))', borderRadius: 8, overflow: 'hidden', cursor: 'pointer', background: 'none' }}
                >
                  <img src={resolvePhoto(p)} alt="" loading="lazy" style={{ width: '100%', height: 84, objectFit: 'cover', display: 'block' }} />
                  {!isBaked && isCached(p) && (
                    <span
                      title="Cached locally"
                      style={{ position: 'absolute', top: 4, right: 4, width: 8, height: 8, borderRadius: '50%', background: '#22D3A5' }}
                    />
                  )}
                </button>
              ))}
          </div>

          {folder !== '__local' && pageCount > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginTop: 12 }}>
              <button type="button" style={btn()} disabled={safePage === 0} onClick={() => setPage(safePage - 1)}>
                Previous
              </button>
              <span style={S.hint}>{safePage + 1} / {pageCount}</span>
              <button type="button" style={btn()} disabled={safePage >= pageCount - 1} onClick={() => setPage(safePage + 1)}>
                Next
              </button>
            </div>
          )}

          {isBaked && (
            <div style={{ ...S.hint, marginTop: 10 }}>
              {BAKED_COUNT} photos bundled with the app, already downscaled. Instant,
              and they work with no network.
            </div>
          )}

          {!isBaked && folder !== '__local' && (
            <div style={{ ...S.hint, marginTop: 10 }}>
              Twelve at a time — these are full-size originals (0.8–4.7 MB) with no
              thumbnail on the server, so loading a whole folder would stall the
              browser. Picking one stores a downscaled copy, marked with a green dot.
            </div>
          )}

          {folder !== '__local' && !serverList.length && (
            <div style={{ ...S.hint, padding: 16 }}>No filenames match that filter.</div>
          )}
        </div>
      </div>
    </>
  );
};

// ─── Photo slot editor ───────────────────────────────────────────────────────

const PhotoSlot = ({ bucket, id, slot, label, isArray, value, onOpen, clientId }) => {
  const list = isArray ? (Array.isArray(value) ? value : []) : (value ? [value] : []);

  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ ...S.label, marginBottom: 0 }}>{label}</span>
        <div style={{ display: 'flex', gap: 6 }}>
          {clientId != null && (
            <button
              type="button"
              style={{ ...btn(), padding: '3px 8px', fontSize: 10 }}
              title="Put this photo in the same slot on every job for this client"
              onClick={() => onOpen(slot, { applyAll: true })}
            >
              Apply to all
            </button>
          )}
          <button
            type="button"
            style={{ ...btn('blue'), padding: '3px 8px', fontSize: 10 }}
            onClick={() => onOpen(slot, { index: list.length })}
          >
            {isArray ? '+ Add' : 'Set'}
          </button>
        </div>
      </div>

      {list.length ? (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {list.map((p, i) => (
            <div key={`${slot}-${i}`} style={{ position: 'relative' }}>
              <img
                src={resolvePhoto(p)}
                alt=""
                loading="lazy"
                onClick={() => onOpen(slot, { index: i })}
                style={{
                  width: 78, height: 60, objectFit: 'cover', borderRadius: 7, cursor: 'pointer',
                  border: '1px solid var(--nd-w12, rgba(255,255,255,0.12))', display: 'block',
                }}
              />
              <button
                type="button"
                aria-label="Remove"
                onClick={() => setPhotoSlot(bucket, id, slot, null, i)}
                style={{
                  position: 'absolute', top: -5, right: -5, width: 18, height: 18, borderRadius: '50%',
                  background: 'rgba(248,113,113,0.92)', color: '#0B0D11', border: 'none',
                  cursor: 'pointer', fontSize: 11, fontWeight: 700, lineHeight: '18px', padding: 0,
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ ...S.hint, padding: '10px 12px', border: '1px dashed var(--nd-w12, rgba(255,255,255,0.12))', borderRadius: 8 }}>
          Empty
        </div>
      )}
    </div>
  );
};

// ─── Main drawer ─────────────────────────────────────────────────────────────

const DemoEditorDrawer = ({ onClose }) => {
  const [, force] = useState(0);
  const [tab, setTab] = useState('jobs');
  const [clientId, setClientId] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [picker, setPicker] = useState(null); // {bucket,id,slot,index,applyAll}
  const [caching, setCaching] = useState(null); // {done,total,failed}
  const importRef = useRef(null);

  useEffect(() => subscribe(() => force((n) => n + 1)), []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !picker) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, picker]);

  const data = getData();
  const seed = getSeedData();

  const activeClientId = clientId != null ? clientId : (data.clients[0] && data.clients[0].client_id);
  const clientJobs = useMemo(
    () => data.jobs.filter((j) => j.client_id === activeClientId),
    [data.jobs, activeClientId],
  );
  const activeJobId = jobId != null && clientJobs.some((j) => j.schedule_id === jobId)
    ? jobId
    : (clientJobs[0] && clientJobs[0].schedule_id);
  const job = clientJobs.find((j) => j.schedule_id === activeJobId);

  const clientAttendance = useMemo(
    () => data.attendance.filter((a) => a.client_id === activeClientId),
    [data.attendance, activeClientId],
  );

  const openPicker = useCallback((bucket, id, slot, opts = {}) => {
    setPicker({ bucket, id, slot, index: opts.index || 0, applyAll: Boolean(opts.applyAll) });
  }, []);

  const handlePick = (value) => {
    if (!picker) return;
    if (picker.applyAll) {
      applyPhotoToClient(activeClientId, picker.slot, value);
      demoToast('Applied to every job for this client.', 'good');
    } else {
      setPhotoSlot(picker.bucket, picker.id, picker.slot, value, picker.index);
    }
  };

  // Warn when a job's instants are not in the order the FSR timeline expects.
  const chainWarning = useMemo(() => {
    if (!job) return null;
    const seq = JOB_TIME_FIELDS.map(([f]) => job[f]).filter(Boolean).map((v) => Date.parse(v));
    for (let i = 1; i < seq.length; i += 1) if (seq[i] < seq[i - 1]) return 'Timestamps are out of order.';
    return null;
  }, [job]);

  const doExport = () => {
    const text = exportJSON();
    const blob = new Blob([text], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `nova-demo-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    demoToast(`Exported (${fmtBytes(text.length)}).`, 'good');
  };

  const doImport = async (file) => {
    try {
      await importJSON(await file.text());
      demoToast('Demo data imported.', 'good');
    } catch (err) {
      demoToast(`Import failed: ${err.message}`, 'warn');
    }
  };

  const doCacheAll = async () => {
    setCaching({ done: 0, total: referencedServerPhotos().length, failed: 0 });
    try {
      const r = await cacheAllPhotos((p) => setCaching(p));
      demoToast(
        r.total === 0
          ? 'Everything is already cached.'
          : `Cached ${r.done - r.failed} photo(s)${r.failed ? `, ${r.failed} failed` : ''}.`,
        r.failed ? 'warn' : 'good',
      );
    } catch (err) {
      demoToast(`Caching failed: ${err.message}`, 'warn');
    } finally {
      setCaching(null);
    }
  };

  const doReset = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Reset every demo edit and delete all uploaded photos?')) return;
    await resetAll();
    demoToast('Demo data reset to the original seed.', 'good');
  };

  return (
    <>
      <div style={S.scrim} onClick={onClose} />
      <aside style={S.panel}>
        <div style={S.head}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: '-0.01em' }}>Demo data editor</div>
            <div style={S.hint}>Local only. Nothing here reaches the server.</div>
          </div>
          <button type="button" onClick={onClose} style={btn()}><X size={13} /> Close</button>
        </div>

        <div style={S.tabs}>
          <button type="button" style={tabBtn(tab === 'jobs')} onClick={() => setTab('jobs')}>
            <Clock size={12} /> Jobs
          </button>
          <button type="button" style={tabBtn(tab === 'attendance')} onClick={() => setTab('attendance')}>
            <Users size={12} /> Attendance
          </button>
          <button type="button" style={tabBtn(tab === 'clients')} onClick={() => setTab('clients')}>
            <ImageIcon size={12} /> Clients
          </button>
          <button type="button" style={tabBtn(tab === 'data')} onClick={() => setTab('data')}>
            <Database size={12} /> Data
          </button>
        </div>

        {tab !== 'data' && (
          <div style={{ padding: '10px 18px 0' }}>
            <span style={S.label}>Client</span>
            <select
              style={S.input}
              value={activeClientId || ''}
              onChange={(e) => { setClientId(Number(e.target.value)); setJobId(null); }}
            >
              {data.clients.map((c) => (
                <option key={c.client_id} value={c.client_id}>{c.client_name}</option>
              ))}
            </select>
          </div>
        )}

        <div style={S.body}>
          {/* ── Jobs ─────────────────────────────────────────────── */}
          {tab === 'jobs' && job && (
            <>
              <div style={{ marginBottom: 12 }}>
                <span style={S.label}>Job</span>
                <select
                  style={S.input}
                  value={activeJobId}
                  onChange={(e) => setJobId(Number(e.target.value))}
                >
                  {clientJobs.map((j) => (
                    <option key={j.schedule_id} value={j.schedule_id}>
                      {j.scheduled_date} · {j.site_name} · {j.status}
                    </option>
                  ))}
                </select>
              </div>

              <div style={S.card}>
                <div style={S.section}>Timestamps <span style={{ ...S.hint, fontWeight: 400 }}>(Asia/Karachi)</span></div>
                {chainWarning && (
                  <div style={{ ...S.hint, color: '#F59E0B', display: 'flex', gap: 6, alignItems: 'center', marginBottom: 10 }}>
                    <AlertTriangle size={12} /> {chainWarning} The page will still render it.
                  </div>
                )}
                {JOB_TIME_FIELDS.map(([f, label]) => (
                  <div key={f} style={{ marginBottom: 9 }}>
                    <span style={S.label}>{label}</span>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input
                        style={S.input}
                        type="datetime-local"
                        value={toPktInput(job[f])}
                        onChange={(e) => setOverride('jobs', job.schedule_id, { [f]: fromPktInput(e.target.value) })}
                      />
                      <button
                        type="button"
                        title="Revert to the seeded value"
                        style={{ ...btn(), padding: '6px 8px' }}
                        onClick={() => clearOverrideField('jobs', job.schedule_id, f)}
                      >
                        <RotateCcw size={12} />
                      </button>
                    </div>
                  </div>
                ))}
                <div style={{ marginTop: 10 }}>
                  <span style={S.label}>Scheduled date</span>
                  <input
                    style={S.input}
                    type="date"
                    value={job.scheduled_date || ''}
                    onChange={(e) => setOverride('jobs', job.schedule_id, { scheduled_date: e.target.value })}
                  />
                </div>
              </div>

              <div style={S.card}>
                <div style={S.section}>Photos</div>
                {JOB_PHOTO_FIELDS.map(([slot, label, isArray]) => (
                  <PhotoSlot
                    key={slot}
                    bucket="jobs"
                    id={job.schedule_id}
                    slot={slot}
                    label={label}
                    isArray={isArray}
                    value={job[slot]}
                    clientId={activeClientId}
                    onOpen={(s, opts) => openPicker('jobs', job.schedule_id, s, opts)}
                  />
                ))}
              </div>

              <div style={S.card}>
                <div style={S.section}>Text</div>
                {JOB_TEXT_FIELDS.map(([f, label]) => (
                  <div key={f} style={{ marginBottom: 9 }}>
                    <span style={S.label}>{label}</span>
                    <input
                      style={S.input}
                      value={job[f] == null ? '' : job[f]}
                      onChange={(e) => setOverride('jobs', job.schedule_id, { [f]: e.target.value })}
                    />
                  </div>
                ))}
                <div style={{ marginBottom: 4 }}>
                  <span style={S.label}>Panels cleaned</span>
                  <input
                    style={S.input}
                    type="number"
                    value={job.total_panels_cleaned == null ? '' : job.total_panels_cleaned}
                    onChange={(e) => setOverride('jobs', job.schedule_id, { total_panels_cleaned: Number(e.target.value) })}
                  />
                </div>
              </div>
            </>
          )}

          {/* ── Attendance ───────────────────────────────────────── */}
          {tab === 'attendance' && (
            <>
              <div style={{ ...S.hint, marginBottom: 10 }}>
                {clientAttendance.length} shift(s). A blank clock-out is an open shift —
                that is what the dashboard&apos;s &ldquo;No clock-out&rdquo; column counts.
              </div>
              {clientAttendance.map((r) => (
                <div key={r.id} style={S.card}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>{r.fo_name}</div>
                    <div style={S.hint}>{r.attendance_date} · {r.site_name}</div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
                    <div>
                      <span style={S.label}>Clock in</span>
                      <input
                        style={S.input}
                        type="datetime-local"
                        value={toPktInput(r.clock_in_at)}
                        onChange={(e) => setOverride('attendance', r.id, { clock_in_at: fromPktInput(e.target.value) })}
                      />
                    </div>
                    <div>
                      <span style={S.label}>Clock out</span>
                      <input
                        style={S.input}
                        type="datetime-local"
                        value={toPktInput(r.clock_out_at)}
                        onChange={(e) => {
                          const iso = fromPktInput(e.target.value);
                          const hrs = iso && r.clock_in_at
                            ? Number(((Date.parse(iso) - Date.parse(r.clock_in_at)) / 3600000).toFixed(2))
                            : null;
                          // hours_worked is a stored column in the real schema, so
                          // it has to be kept in step or the hours totals drift.
                          setOverride('attendance', r.id, { clock_out_at: iso, hours_worked: hrs });
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <PhotoSlot
                      bucket="attendance" id={r.id} slot="clock_in_image_url" label="In photo"
                      isArray={false} value={r.clock_in_image_url} clientId={null}
                      onOpen={(s, o) => openPicker('attendance', r.id, s, o)}
                    />
                    <PhotoSlot
                      bucket="attendance" id={r.id} slot="clock_out_image_url" label="Out photo"
                      isArray={false} value={r.clock_out_image_url} clientId={null}
                      onOpen={(s, o) => openPicker('attendance', r.id, s, o)}
                    />
                  </div>

                  <div style={{ marginTop: 6 }}>
                    <span style={S.label}>Name</span>
                    <input
                      style={S.input}
                      value={r.fo_name}
                      onChange={(e) => setOverride('attendance', r.id, { fo_name: e.target.value })}
                    />
                  </div>
                </div>
              ))}
            </>
          )}

          {/* ── Clients ──────────────────────────────────────────── */}
          {tab === 'clients' && (
            <>
              <div style={{ ...S.hint, marginBottom: 10 }}>
                Renaming a client follows through onto its jobs and attendance rows,
                so every page keeps agreeing about what it is called.
              </div>
              {data.clients.map((c) => {
                const original = seed.clients.find((x) => x.client_id === c.client_id);
                return (
                  <div key={c.client_id} style={S.card}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 800 }}>#{c.client_id}</div>
                      {original && c.client_name !== original.client_name && (
                        <button
                          type="button"
                          style={{ ...btn(), padding: '3px 8px', fontSize: 10 }}
                          onClick={() => clearOverrideField('clients', c.client_id, 'client_name')}
                        >
                          <RotateCcw size={10} /> Revert name
                        </button>
                      )}
                    </div>
                    {[['client_name', 'Client name'], ['contact_person', 'Contact'], ['contact_number', 'Phone'], ['city', 'City'], ['address', 'Address']].map(([f, label]) => (
                      <div key={f} style={{ marginBottom: 8 }}>
                        <span style={S.label}>{label}</span>
                        <input
                          style={S.input}
                          value={c[f] == null ? '' : c[f]}
                          onChange={(e) => setOverride('clients', c.client_id, { [f]: e.target.value })}
                        />
                      </div>
                    ))}
                  </div>
                );
              })}
            </>
          )}

          {/* ── Data ─────────────────────────────────────────────── */}
          {tab === 'data' && (
            <>
              <div style={S.card}>
                <div style={S.section}>Photo defaults</div>
                <div style={{ ...S.hint, marginBottom: 10 }}>
                  Prefill seeds every slot from the real server photo library so the demo
                  looks complete without any work. Blank empties them so you fill each one
                  yourself. Photos you set explicitly survive either way.
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    style={btn(getPhotoMode() === 'prefill' ? 'blue' : 'plain')}
                    onClick={() => setPhotoMode('prefill')}
                  >
                    {getPhotoMode() === 'prefill' && <Check size={12} />} Prefill from server
                  </button>
                  <button
                    type="button"
                    style={btn(getPhotoMode() === 'blank' ? 'blue' : 'plain')}
                    onClick={() => setPhotoMode('blank')}
                  >
                    {getPhotoMode() === 'blank' && <Check size={12} />} Start blank
                  </button>
                </div>
                <div style={{ marginTop: 10 }}>
                  <button type="button" style={btn('red')} onClick={clearPhotoOverrides}>
                    <Trash2 size={12} /> Clear my photo choices
                  </button>
                </div>
              </div>

              <div style={S.card}>
                <div style={S.section}>Photo speed</div>
                <div style={{ ...S.hint, marginBottom: 10 }}>
                  The demo ships with {BAKED_COUNT} pre-downscaled photos in
                  <code> public/demo-photos</code>, so nothing needs downloading and it
                  works offline out of the box. This only matters if you have picked
                  originals from the server gallery — those are 0.8–4.7 MB each and
                  should be cached before demoing.
                </div>

                {caching ? (
                  <>
                    <div style={{ height: 6, borderRadius: 3, background: 'var(--nd-w06, rgba(255,255,255,0.06))', overflow: 'hidden', marginBottom: 7 }}>
                      <div style={{
                        width: `${caching.total ? (caching.done / caching.total) * 100 : 0}%`,
                        height: '100%', background: '#60A5FA', transition: 'width .2s ease',
                      }}
                      />
                    </div>
                    <div style={S.hint}>
                      Caching {caching.done} of {caching.total}
                      {caching.failed ? ` · ${caching.failed} failed` : ''} — leave this open.
                    </div>
                  </>
                ) : (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button type="button" style={btn('green')} onClick={doCacheAll}>
                      <Download size={12} /> Cache all demo photos
                    </button>
                    <button type="button" style={btn()} onClick={clearServerCache}>
                      <Trash2 size={12} /> Clear cache
                    </button>
                  </div>
                )}

                <div style={{ ...S.hint, marginTop: 10 }}>
                  {referencedServerPhotos().length === 0
                    ? 'No server originals in use — everything is running off the baked set.'
                    : `${referencedServerPhotos().filter(isCached).length} of ${referencedServerPhotos().length} server originals cached.`}
                  {' '}{photoCount()} stored object(s), {fmtBytes(photoStorageBytes())} in IndexedDB.
                </div>
              </div>

              <div style={S.card}>
                <div style={S.section}>Move this demo somewhere else</div>
                <div style={{ ...S.hint, marginBottom: 10 }}>
                  Export writes one JSON file with your edits and your uploaded photos
                  inlined, so it restores on any machine — useful for the demo laptop.
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button type="button" style={btn('green')} onClick={doExport}>
                    <Download size={12} /> Export
                  </button>
                  <button type="button" style={btn()} onClick={() => importRef.current && importRef.current.click()}>
                    <Upload size={12} /> Import
                  </button>
                  <input
                    ref={importRef}
                    type="file"
                    accept="application/json"
                    onChange={(e) => e.target.files && e.target.files[0] && doImport(e.target.files[0])}
                    style={{ display: 'none' }}
                  />
                  <button type="button" style={btn('red')} onClick={doReset}>
                    <RotateCcw size={12} /> Reset everything
                  </button>
                </div>
              </div>

              <div style={S.card}>
                <div style={S.section}>Shortcuts</div>
                <div style={S.hint}>
                  <strong>Ctrl+Shift+E</strong> — show/hide the edit button.<br />
                  <strong>Ctrl+Shift+D</strong> — show/hide the DEMO pill.<br />
                  Both are off-by-default safe: close this drawer and press Ctrl+Shift+E
                  before presenting and no demo chrome remains on screen.
                </div>
              </div>
            </>
          )}
        </div>
      </aside>

      {picker && (
        <PhotoPicker
          slot={picker.slot}
          onPick={handlePick}
          onClose={() => setPicker(null)}
        />
      )}
    </>
  );
};

export default DemoEditorDrawer;
